<template>
  <ValidationProvider
    ref="validatorProvider"
    :vid="id"
    :name="validatorName"
    :rules="validationRules"
    v-slot="{ valid, pristine, errors }"
    :slim="true"
  >
    <div :class="cssClasses.wrapperClasses">
      <div v-if="errors[0]" :class="cssClasses.messageClasses">{{ errors[0] }}</div>
      <div :class="cssClasses.inputHolderClasses">
        <input
          :style="{
            borderColor:
              validState && pristine
                ? '#23303d'
                : validState && !valid
                ? '#e5472d'
                : valid && pristine && validState
                ? '#6dd400'
                : '',
            backgroundColor:
              validState && pristine ? 'white' : validState && !valid ? '#e5472d26' : 'white',
          }"
          ref="input"
          v-if="withMask"
          :placeholder="placeholder"
          :name="name"
          :id="id"
          :required="required"
          :value="value"
          :type="type"
          :autocomplete="autocomplete"
          :disabled="disabled"
          :readonly="readonly"
          :class="cssClasses.inputClasses"
          :autofocus="autofocus"
          v-bind="$attrs"
          v-model="innerValue"
          @keydown.space="event => (nospace ? event.preventDefault() : null)"
          @click="click"
          v-mask="mask"
        />
        <input
          :style="{
            borderColor:
              validState && pristine
                ? '#23303d'
                : validState && !valid
                ? '#e5472d'
                : valid && !pristine && validState
                ? '#6dd400'
                : '',
            backgroundColor:
              validState && pristine ? 'white' : validState && !valid ? '#e5472d26' : 'white',
          }"
          ref="input"
          v-if="!withMask"
          :data-vv-as="placeholder"
          :name="name"
          :id="id"
          :required="required"
          :value="value"
          :type="type"
          :autocomplete="autocomplete"
          :disabled="disabled"
          :readonly="readonly"
          :class="cssClasses.inputClasses"
          :autofocus="autofocus"
          v-bind="$attrs"
          v-model="innerValue"
          @keydown.space="event => (nospace ? event.preventDefault() : null)"
          @click="click"
        />
        <span v-if="bottomBarVisible" :class="$style.bar"></span>
        <label :for="name">{{ label || placeholder }}</label>
        <slot />
        <div
          v-if="validState && !validationRules.includes('confirmed') && !valid && !pristine"
          :class="cssClasses.validationStatus"
        >
          <div :class="minValue && 'passed'" class="validation-rule">
            En az 8 karakter olmalıdır.
          </div>
          <div :class="atLeast && 'passed'" class="validation-rule">
            Şifreniz aşağıdakilerden en az 2 tanesini içermelidir.
            <ul style="paddingLeft: 25px">
              <li>Rakam</li>
              <li>Küçük harf</li>
              <li>Büyük harf</li>
              <li>Özel karakter (!,*,?...)</li>
            </ul>
          </div>
        </div>
        <div
          v-if="
            validState &&
              validationRules.includes('confirmed') &&
              !valid &&
              !pristine &&
              errors[0] == 'Şifre doğrulaması hatalı'
          "
          :class="cssClasses.validationStatus"
        >
          <div class="validation-rule validation-rule-confirm">
            Lütfen şifrenizi doğrulayın
          </div>
        </div>
      </div>
    </div>
  </ValidationProvider>
</template>

<script>
import { ValidationProvider } from 'vee-validate';
import { mask } from 'vue-the-mask';
export default {
  name: 'VueInput',
  inheritAttrs: false,
  components: {
    ValidationProvider,
  },
  directives: { mask },
  props: {
    name: {
      type: String,
      required: true,
    },
    id: {
      type: String,
      required: true,
    },
    validatorName: {
      type: String,
    },
    placeholder: {
      type: String,
    },
    label: {
      type: String,
    },
    required: {
      type: Boolean,
    },
    autofocus: {
      type: Boolean,
    },
    value: {
      type: String,
    },
    type: {
      type: String,
      default: 'text',
    },
    disabled: {
      type: Boolean,
    },
    readonly: {
      type: Boolean,
    },
    messageOnTop: {
      type: Boolean,
      default: true,
    },
    validationRules: {
      type: [Object, String],
      default: '',
    },
    autocomplete: {
      type: String,
      default: 'off',
    },
    borderType: {
      type: String,
      default: 'box',
    },
    iconName: {
      type: String,
    },
    iconSize: {
      type: Object,
      default: () => ({
        width: 18,
        height: 18,
      }),
    },
    nospace: {
      type: Boolean,
      default: false,
    },
    mask: {
      type: String,
      default: null,
    },
    inputLarge: {
      type: Boolean,
      default: false,
    },
    validState: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      observer: null,
      innerValue: '',
    };
  },
  watch: {
    // Handles internal model changes.
    innerValue(newVal) {
      this.$emit('input', newVal);
    },
    // Handles external model changes.
    value(newVal) {
      this.innerValue = newVal;
    },
  },
  computed: {
    minValue() {
      return this.innerValue.length >= 8;
    },
    atLeast() {
      let list = 0;
      if (this.innerValue.match(/[!@#\\$%\\^&\\*]/g)) {
        list++;
      }
      if (this.innerValue.match(/[0-9]/g)) {
        list++;
      }
      if (this.innerValue.match(/[a-z]/g)) {
        list++;
      }
      if (this.innerValue.match(/[A-Z]/g)) {
        list++;
      }
      if (list > 1) {
        return true;
      }
      return false;
    },
    withMask() {
      return this.mask && this.mask.length > 0;
    },
    bottomBarVisible() {
      return !(this.borderType === 'box' || this.borderType === 'left-bordered');
    },
    cssClasses() {
      const wrapperClasses = [this.$style['input-wrapper']];
      const validationStatus = [this.$style['validation-status']];
      const inputHolderClasses = [this.$style['input-holder']];
      const messageClasses = [this.$style.message, this.$style.error];
      const inputClasses = [];

      if (this.disabled) {
        inputHolderClasses.push(this.$style.disabled);
        inputClasses.push(this.$style['ios-disabled-fix']);
      }

      if (this.borderType === 'box') {
        inputHolderClasses.push(this.$style['input-holder-box-border']);
      }

      if (this.borderType === 'left-bordered') {
        inputHolderClasses.push(this.$style['input-holder-left-bordered']);
      }
      if (this.value || this.placeholder) {
        inputClasses.push(this.$style.hasValue);
      }

      if (this.label && !this.placeholder) {
        inputClasses.push(this.$style['input-material']);
      }

      if (this.messageOnTop) {
        messageClasses.push(this.$style['message-top']);
        inputHolderClasses.push(this.$style['input-holder-bottom']);
      }

      if (this.inputLarge) {
        inputHolderClasses.push(this.$style['input-large']);
      }
      if (this.type === 'text') {
        inputClasses.push(this.$style['ios-topshadow-fix']);
      }
      if (this.validationRules.includes('confirmed')) {
        validationStatus.push(this.$style['validation-status-confirmed']);
      }

      return { wrapperClasses, inputHolderClasses, messageClasses, inputClasses, validationStatus };
    },
  },

  created() {
    if (this.value) {
      this.innerValue = this.value;
    }
  },
  mounted() {
    if (window.IntersectionObserver) {
      this.handleObserver();
    }
  },
  beforeDestroy() {
    this.observer = null;
  },
  methods: {
    handleObserver() {
      this.observer = new IntersectionObserver(
        () => {
          if (this.autofocus && this.$refs.input) {
            this.$refs.input.focus();
          }
        },
        { root: this.$refs.input.parentElement, threshold: 1 },
      );
      this.observer.observe(this.$refs.input);
    },
    click(e) {
      this.$emit('click', e);
    },
  },
};
</script>

<style lang="scss" module>
@import '~@/styles/styles';
.input-wrapper {
  display: flex;
  flex-direction: column;
}
.input-holder {
  position: relative;
  order: 1;
  position: relative;
  .validation-status {
    position: absolute;
    right: 0;
    top: 0;
    transform: translateX(100%);
    padding: 10px;
    padding-left: 53px;
    width: 260px;
    background-image: url('~@/assets/bg/shape.png');
    background-size: cover;
    background-position: left center;
    background-repeat: no-repeat;
    &-confirmed {
      background-image: url('~@/assets/bg/combined-shape-small.png');
      top: 50%;
      transform: translate(100%, -50%);
      height: 92px;
      display: flex;
      align-items: center;
      padding-left: 45px;
    }
  }

  input {
    background-color: $input-background-color;
    border: none;
    border-bottom: $input-border;
    padding: $input-padding;
    display: block;
    width: 100%;
    font-family: $input-font-family;
    font-size: $input-font-size;
    font-weight: $input-font-weight;
    color: $input-color;
    height: $input-height;
    border-radius: 0;
    margin: $input-margin;
    order: 1;
    outline: none !important;

    &:active,
    &:focus,
    &:hover {
      outline: none !important;
    }

    &:focus ~ label,
    &.hasValue ~ label {
      top: $input-placeholder-active-top;
      font-size: $input-placeholder-active-font-size;
    }

    &.input-material {
      padding-top: 30px;
    }

    &:focus ~ .bar:before,
    &:focus ~ .bar:after {
      width: 50%;
    }

    &[type='tel'] {
      letter-spacing: 2px;
    }
  }

  input[type='password']::-ms-clear,
  input[type='password']::-ms-reveal {
    display: none;
  }

  input:focus::-webkit-textfield-decoration-container {
    visibility: hidden;
  }

  label {
    color: $input-placeholder-color;
    font-size: $input-placeholder-font-size;
    font-weight: $input-placeholder-font-weight;
    position: absolute;
    pointer-events: none;
    top: $input-placeholder-top;
    left: $input-placeholder-left;
    right: $input-placeholder-left;
    transform: translateY(-50%);
    transition: $animation-transition-duration * 2 $animation-easing all;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: auto;
    max-width: 100%;
  }

  &.input-holder-bottom {
    order: 2;
  }

  &.input-holder-box-border {
    input {
      border: $input-border;
      border-radius: $input-border-radius;
    }
  }
  &.input-holder-left-bordered {
    border-radius: $input-border-radius;
    @include bg-linear-gradient(
      to top,
      palette-color-level('red', '30') 0%,
      palette-color-level('yellow', '50') 100%
    );

    padding-left: $input-padding-left-bordered;

    input {
      border: $input-border;
      border-left: none;
      border-radius: none;
      border-top-right-radius: $input-border-radius;
      border-bottom-right-radius: $input-border-radius;
    }

    label {
      left: $input-placeholder-left + $input-padding-left-bordered;
    }
  }
  a {
    position: absolute;
    right: 20px;
    top: 50%;
    transform: translateY(-50%);
  }
}

.error {
  label {
    color: $input-error-color;
  }
  input {
    border-color: $input-error-color !important;

    &:focus ~ label,
    &.hasValue ~ label {
      color: $input-error-color;
    }
  }

  .bar {
    &:before,
    &:after {
      background: $input-error-color;
    }
  }

  .message {
    color: $input-error-color;
  }
}

.bar {
  position: relative;
  display: block;
  width: 100%;

  &:before,
  &:after {
    content: '';
    height: $input-bar-height;
    width: 0;
    bottom: 0;
    position: absolute;
    background: $input-bar-color;
    transition: all 0.2s ease-in-out;
  }
  &:before {
    left: 50%;
  }
  &:after {
    right: 50%;
  }
}

.message {
  display: block;
  height: $input-message-height;
  padding: $input-message-padding;
  position: relative;
  color: $input-error-color;
  font-size: $input-message-font-size;
  font-weight: $input-message-font-weight;
  order: 2;
}

.message-top {
  order: 1;
}
.disabled {
  opacity: 0.6;
}

.input-large {
  input {
    font-weight: palette-font-weight-level('3');
    letter-spacing: 4px;
    padding: palette-space-level('25') palette-space-level('20') palette-space-level('20');
    font-size: palette-font-size-level('9');
  }
  label {
    display: none;
  }
}
.ios-disabled-fix {
  //to override IOS SAFARI  0.4 opacity on disabled input
  opacity: unset;
  -webkit-text-fill-color: palette-color-level('grey', 40);
}
.ios-topshadow-fix {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}
</style>
